// SEO工具模块
export const SEOUtils = {
  // 设置页面标题
  setTitle(title, suffix = ' - 弥央大学') {
    document.title = title + suffix
  },

  // 设置meta描述
  setDescription(description) {
    this.setMetaTag('name', 'description', description)
    this.setMetaTag('property', 'og:description', description)
    this.setMetaTag('property', 'twitter:description', description)
  },

  // 设置meta关键词
  setKeywords(keywords) {
    const keywordString = Array.isArray(keywords) ? keywords.join(',') : keywords
    this.setMetaTag('name', 'keywords', keywordString)
  },

  // 设置Open Graph标签
  setOGTags(data) {
    const { title, description, image, url, type = 'article' } = data
    
    if (title) {
      this.setMetaTag('property', 'og:title', title)
      this.setMetaTag('property', 'twitter:title', title)
    }
    
    if (description) {
      this.setMetaTag('property', 'og:description', description)
      this.setMetaTag('property', 'twitter:description', description)
    }
    
    if (image) {
      this.setMetaTag('property', 'og:image', image)
      this.setMetaTag('property', 'twitter:image', image)
    }
    
    if (url) {
      this.setMetaTag('property', 'og:url', url)
      this.setMetaTag('property', 'twitter:url', url)
    }
    
    this.setMetaTag('property', 'og:type', type)
  },

  // 设置canonical URL
  setCanonical(url) {
    let canonical = document.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = url
  },

  // 通用设置meta标签方法
  setMetaTag(attribute, name, content) {
    let meta = document.querySelector(`meta[${attribute}="${name}"]`)
    if (!meta) {
      meta = document.createElement('meta')
      meta.setAttribute(attribute, name)
      document.head.appendChild(meta)
    }
    meta.content = content
  },

  // 添加结构化数据
  addStructuredData(data) {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify(data)
    document.head.appendChild(script)
  },

  // 页面SEO优化通用方法
  optimizePage(config) {
    const {
      title,
      description,
      keywords,
      image = 'https://mio.edu.kg/src/static/icon.png',
      url = window.location.href,
      type = 'website'
    } = config

    // 设置基础SEO标签
    if (title) this.setTitle(title)
    if (description) this.setDescription(description)
    if (keywords) this.setKeywords(keywords)
    
    // 设置Open Graph标签
    this.setOGTags({ title, description, image, url, type })
    
    // 设置canonical URL
    this.setCanonical(url)
  }
}

// 页面特定的SEO配置
export const pageConfigs = {
  home: {
    title: '首页',
    description: '欢迎来到弥央大学(mio.edu.kg)！分享异度之刃3游戏心得、日常生活碎片和笛声战技笔记。更多精彩技术文章和深度游戏评测请访问主博客 www.eunie.online',
    keywords: ['弥央大学', 'mio.edu.kg', '异度之刃3', 'Xenoblade 3', 'Mio', 'Eunie', '游戏攻略', '日常博客', '笛声', '战技', '个人网站', 'www.eunie.online', '主博客', '技术分享', '游戏评测'],
    url: 'https://mio.edu.kg/'
  },
  
  about: {
    title: '关于弥央大学',
    description: '了解弥央大学的由来、edu.kg域名说明及免费教育资源获取方法。edu邮箱申请教程和白嫖攻略分享。主博客：www.eunie.online',
    keywords: ['关于弥央大学', '域名说明', 'edu邮箱', '教育邮箱', '白嫖教程', '免费入学', 'mio.edu.kg', '网站介绍', '技术栈', 'eunie.online'],
    url: 'https://mio.edu.kg/about'
  },
  
  links: {
    title: '友情链接',
    description: '弥央大学的友情链接页面，包含教育资源、edu邮箱申请、白嫖教程相关的官方链接和同好社区。',
    keywords: ['友情链接', 'edu邮箱', '教育邮箱', '白嫖教程', '免费资源', '异度之刃社区', 'Reddit', '贴吧', '友站'],
    url: 'https://mio.edu.kg/links'
  },
  
  apply: {
    title: '申请入学',
    description: '免费申请mio.edu.kg教育邮箱服务，获取Notion教育Plus版等教育优惠。基于cloudflare_temp_email搭建，完全免费使用。',
    keywords: ['申请入学', 'edu邮箱申请', 'mio.edu.kg邮箱', 'Notion教育版', '教育优惠', '免费邮箱', 'cloudflare_temp_email'],
    url: 'https://mio.edu.kg/apply'
  },
  
  blog: {
    title: '博客文章',
    description: '弥央大学的博客文章，涵盖游戏心得、技术分享、生活随想等多个主题。',
    keywords: ['博客', '文章', '游戏心得', '技术分享', '生活随想'],
    type: 'article'
  }
}

// 文章特定的SEO优化
export const optimizeArticle = (article) => {
  const config = {
    title: article.title,
    description: article.excerpt || article.description || `阅读弥央大学的文章：${article.title}`,
    keywords: article.tags || ['弥央大学', '博客文章'],
    url: `https://mio.edu.kg/blog/${article.id}`,
    type: 'article',
    image: article.image || 'https://mio.edu.kg/src/static/icon.png'
  }

  SEOUtils.optimizePage(config)

  // 添加文章特定的结构化数据
  if (article.publishDate || article.author) {
    const articleData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": config.description,
      "url": config.url,
      "datePublished": article.publishDate,
      "author": {
        "@type": "Person",
        "name": article.author || "弥央"
      },
      "publisher": {
        "@type": "Organization",
        "name": "弥央大学",
        "logo": {
          "@type": "ImageObject",
          "url": "https://mio.edu.kg/src/static/icon.png"
        }
      }
    }
    
    SEOUtils.addStructuredData(articleData)
  }
}
